import React, { useState } from 'react';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  XCircle,
  Settings
} from 'lucide-react';
import { Button, DataTable } from '../../components/shared';
import { useAdminTermsOfService } from '../../hooks/useAdminTermsOfService';
import { useToastContext } from '../../contexts/ToastContext';
import CreateTermsOfServiceModal from '../../components/admin/CreateTermsOfServiceModal';
import CreateTermsSettingsModal from '../../components/admin/CreateTermsSettingsModal';

const AdminTermsOfService = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const { success, error: showError } = useToastContext();

  const {
    sections,
    isLoading,
    error,
    filters,
    settings,
    isLoadingSettings,
    createSection,
    isCreating,
    updateSection,
    isUpdating,
    toggleStatus,
    isToggling,
    deleteSection,
    isDeleting,
    updateSettings,
    isUpdatingSettings,
  } = useAdminTermsOfService();

  const columns = [
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-64',
      render: (section) => (
        <div className="flex items-center gap-3">
          {section.icon && (
            <div className="flex-shrink-0 w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-white" />
            </div>
          )}
          <p className="text-sm font-medium text-gray-900 truncate">
            {section.title || 'N/A'}
          </p>
        </div>
      )
    },
    {
      key: 'content',
      label: 'Paragraphs',
      sortable: false,
      width: 'w-24',
      render: (section) => (
        <span className="text-sm text-gray-600">
          {section.content?.length || 0} paragraph(s)
        </span>
      )
    },
    {
      key: 'sort_order',
      label: 'Order',
      sortable: true,
      width: 'w-24',
      render: (section) => (
        <span className="text-sm text-gray-900">
          {section.sort_order || 0}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-32',
      render: (section) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          section.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {section.is_active ? (
            <>
              <CheckCircle className="w-3 h-3 mr-1" />
              Active
            </>
          ) : (
            <>
              <XCircle className="w-3 h-3 mr-1" />
              Inactive
            </>
          )}
        </span>
      )
    },
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (section) => {
        setEditingSection(section);
        setShowCreateModal(true);
      },
      variant: 'outline'
    },
    {
      label: (section) => section.is_active ? 'Deactivate' : 'Activate',
      icon: (section) => section.is_active ? XCircle : CheckCircle,
      onClick: (section) => {
        toggleStatus(section.id, {
          onSuccess: () => {
            success(`Section ${section.is_active ? 'deactivated' : 'activated'} successfully`);
          },
          onError: (err) => {
            showError(err.response?.data?.message || 'Failed to update section status');
          }
        });
      },
      variant: 'outline'
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (section) => {
        if (window.confirm('Are you sure you want to delete this section?')) {
          deleteSection(section.id, {
            onSuccess: () => {
              success('Section deleted successfully');
            },
            onError: (err) => {
              showError(err.response?.data?.message || 'Failed to delete section');
            }
          });
        }
      },
      variant: 'danger'
    },
  ];

  const handleSave = (data) => {
    if (editingSection) {
      updateSection({
        id: editingSection.id,
        data
      }, {
        onSuccess: () => {
          success('Section updated successfully');
          setShowCreateModal(false);
          setEditingSection(null);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to update section');
        }
      });
    } else {
      createSection(data, {
        onSuccess: () => {
          success('Section created successfully');
          setShowCreateModal(false);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to create section');
        }
      });
    }
  };

  const handleSettingsSave = (data) => {
    updateSettings(data, {
      onSuccess: () => {
        success('Settings updated successfully');
        setShowSettingsModal(false);
      },
      onError: (err) => {
        showError(err.response?.data?.message || 'Failed to update settings');
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Terms of Service</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage terms of service sections and settings
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setShowSettingsModal(true);
            }}
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setEditingSection(null);
              setShowCreateModal(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Section
          </Button>
        </div>
      </div>

      {/* Settings Info */}
      {settings && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900">
                Last Updated: {settings.last_updated || 'Not set'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
            >
              Edit Settings
            </Button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <DataTable
          data={sections}
          columns={columns}
          actions={actions}
          isLoading={isLoading}
          error={error}
          searchPlaceholder="Search sections..."
        />
      </div>

      {/* Create/Edit Modal */}
      <CreateTermsOfServiceModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingSection(null);
        }}
        onSave={handleSave}
        section={editingSection}
        isEdit={!!editingSection}
        isLoading={isCreating || isUpdating}
      />

      {/* Settings Modal */}
      <CreateTermsSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onSave={handleSettingsSave}
        settings={settings}
        isLoading={isUpdatingSettings}
      />
    </div>
  );
};

export default AdminTermsOfService;

